import { useCallback, useMemo, useState } from 'react'

import type { DetectionStats, SeverityCounts } from '../../lib/api/admin.ts'
import { severityPresentation, SEVERITY_ORDER } from '../../lib/detections/semantics.ts'
import { formatInt } from '../../lib/format.ts'
import { usePolledResource } from '../../lib/usePolledResource.ts'
import { Panel } from '../primitives/Panel.tsx'
import { ErrorState, LoadingRow } from '../primitives/States.tsx'
import { TABLE, TD, TD_NUM, TH, TH_NUM, THEAD, TR } from '../primitives/table.ts'

/** Janelas oferecidas. 30 d fica dentro do teto de 31 dias do servidor (§4.7). */
export const STATS_WINDOWS = [
  { key: '24h', label: '24 h', hours: 24 },
  { key: '7d', label: '7 d', hours: 24 * 7 },
  { key: '30d', label: '30 d', hours: 24 * 30 },
] as const

export type StatsWindow = (typeof STATS_WINDOWS)[number]['key']

/** `from`/`to` calculados NA HORA da chamada: cada poll anda a janela junto. */
export function windowRange(window: StatsWindow): { from: string; to: string } {
  const hours = STATS_WINDOWS.find((w) => w.key === window)?.hours ?? 24
  const to = new Date()
  const from = new Date(to.getTime() - hours * 3_600_000)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function WindowPicker({
  value,
  onChange,
}: {
  value: StatsWindow
  onChange: (window: StatsWindow) => void
}) {
  return (
    <span className="flex items-center gap-0.5" role="radiogroup" aria-label="Janela">
      {STATS_WINDOWS.map((w) => (
        <button
          key={w.key}
          type="button"
          role="radio"
          aria-checked={value === w.key}
          onClick={() => onChange(w.key)}
          className={`border px-1.5 py-px text-micro tnum ${
            value === w.key
              ? 'border-accent text-accent'
              : 'border-line text-fg-muted hover:text-fg'
          }`}
        >
          {w.label}
        </button>
      ))}
    </span>
  )
}

/** Uma faixa de contagens por severidade; a palavra vai junto da cor. */
export function SeverityCountsRow({ counts }: { counts: SeverityCounts }) {
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
      {SEVERITY_ORDER.map((severity) => {
        const p = severityPresentation(severity)
        const value = counts[severity]
        return (
          <div key={severity} className="min-w-0">
            <span className="flex items-center gap-1 text-micro uppercase tracking-label text-fg-muted">
              <span aria-hidden className={`inline-block size-1.5 ${p.fill}`} />
              {p.label}
            </span>
            <span className={`live-value font-mono text-lg leading-none ${value > 0 ? p.text : 'text-fg-muted'}`}>
              {formatInt(value)}
            </span>
          </div>
        )
      })}
      <div className="min-w-0">
        <span className="text-micro uppercase tracking-label text-fg-muted">sem sev.</span>
        <span className="live-value block font-mono text-lg leading-none text-fg-muted">
          {formatInt(counts.unknown)}
        </span>
      </div>
      <div className="min-w-0">
        <span className="text-micro uppercase tracking-label text-fg-muted">total</span>
        <span className="live-value block font-mono text-lg leading-none text-fg">
          {formatInt(counts.total)}
        </span>
      </div>
    </div>
  )
}

/**
 * Detecções por severidade (e, na Black Ghost, por empresa) numa janela.
 *
 * Consulta ao banco por chamada — por isso poll de 60 s e janela escolhida pelo
 * operador, nunca o ritmo de 2 s do stream (§4.7).
 */
export function DetectionStatsPanel({
  load,
  showTenants,
  onOpenTenant,
  className = '',
}: {
  load: (range: { from: string; to: string }, signal: AbortSignal) => Promise<DetectionStats>
  /** Tabela por empresa: só a Black Ghost. */
  showTenants: boolean
  onOpenTenant?: (tenantId: string, tenantName: string | null) => void
  className?: string
}) {
  const [window, setWindow] = useState<StatsWindow>('24h')
  const fetcher = useCallback(
    (signal: AbortSignal) => load(windowRange(window), signal),
    [load, window],
  )
  const { data, error, reload, loadedAt } = usePolledResource(fetcher, 60_000)

  const tenants = useMemo(
    () => [...(data?.tenants ?? [])].sort((a, b) => b.counts.total - a.counts.total),
    [data],
  )

  return (
    <Panel
      title="Detecções por severidade"
      className={className}
      flush
      meta={
        <>
          {loadedAt !== null && (
            <span className="tnum">às {new Date(loadedAt).toLocaleTimeString('pt-BR')}</span>
          )}
          <WindowPicker value={window} onChange={setWindow} />
        </>
      }
    >
      {error !== null && data === null ? (
        <div className="p-3">
          <ErrorState error={error} onRetry={reload} />
        </div>
      ) : data === null ? (
        <LoadingRow label="Contando detecções…" />
      ) : (
        <div className="flex flex-col">
          <div className="border-b border-line-subtle p-3">
            <SeverityCountsRow counts={data.totals} />
          </div>
          {showTenants &&
            (tenants.length === 0 ? (
              <p className="px-3 py-3 text-sm text-fg-muted">Nenhuma detecção na janela.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className={TABLE}>
                  <thead className={THEAD}>
                    <tr>
                      <th scope="col" className={TH}>
                        Empresa
                      </th>
                      {SEVERITY_ORDER.map((severity) => (
                        <th key={severity} scope="col" className={TH_NUM}>
                          {severityPresentation(severity).code}
                        </th>
                      ))}
                      <th scope="col" className={TH_NUM}>
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((row) => (
                      <tr key={row.tenant_id} className={TR}>
                        <th scope="row" className={`${TD} max-w-0 font-normal`}>
                          {onOpenTenant !== undefined ? (
                            <button
                              type="button"
                              onClick={() => onOpenTenant(row.tenant_id, row.tenant_name)}
                              className="block max-w-full truncate text-left text-fg underline-offset-2 hover:underline"
                              title="Abrir o volume desta empresa por agente"
                            >
                              {row.tenant_name ?? row.tenant_id}
                            </button>
                          ) : (
                            <span className="block truncate">{row.tenant_name ?? row.tenant_id}</span>
                          )}
                        </th>
                        {SEVERITY_ORDER.map((severity) => {
                          const value = row.counts[severity]
                          return (
                            <td
                              key={severity}
                              className={`${TD_NUM} ${value > 0 ? severityPresentation(severity).text : 'text-fg-muted'}`}
                            >
                              {formatInt(value)}
                            </td>
                          )
                        })}
                        <td className={TD_NUM}>{formatInt(row.counts.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      )}
    </Panel>
  )
}
